import type { components } from '@/types/api/v1'
import { CalendarOutlined, DeleteOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Flex, Form, Input } from 'antd'
import { isEqual } from 'lodash'
import { useEffect } from 'react'
import { AmIcon, PmIcon } from '../../../Manage/TemplateManagement/ShiftTiles/ShiftIcons'
import type { ScheduleItemWithConflicts } from '../../Context/ScheduleItems/scheduleItemStore'
import { ScheduleItemTractorSelect } from '../Inputs/ScheduleItemTractorSelect'
import { ScheduleItemTrailerSelect } from '../Inputs/ScheduleItemTrailerSelect'

function ScheduleItemRow({
  onRemove,
  index,
  value,
}: {
  value: ScheduleItemWithConflicts
  onRemove: (params: { id: string; index: number }) => void
  index: number
}) {
  if (!value) return null
  return (
    <Vertical
      style={{
        padding: 12,
        background: 'var(--gray-100)',
        borderRadius: 4,
        border: '1px solid var(--gray-300)',
      }}
    >
      {value && (
        <Horizontal justifyContent='space-between' alignItems='flex-start'>
          <Vertical gap={8}>
            <Horizontal verticalCenter justifyContent='space-between'>
              <Texto category='h5'>{value?.driver_name ?? 'Unknown Driver'}</Texto>
              <GraviButton
                appearance='text'
                color='red'
                icon={<DeleteOutlined />}
                onClick={() => onRemove({ id: value.event_id, index })}
              >
                Delete Shift
              </GraviButton>
            </Horizontal>

            <ScheduleItemTractorSelect name={[index, 'tractor']} label='Reassign Tractor' event={value} />
            {value.errors.some((x) => x.type === 'UniqueTrailer') && (
              <ScheduleItemTrailerSelect name={[index, 'trailer']} label='Reassign Trailer' event={value} />
            )}
          </Vertical>
        </Horizontal>
      )}
    </Vertical>
  )
}
type SelectOption = { value?: string | null; label?: string | null }
export type EquipmentConflictsFormInit = {
  items: Array<
    ScheduleItemWithConflicts & {
      tractor?: SelectOption
      trailer?: SelectOption
    }
  >
  removedItemIds: string[]
}

export function EquipmentConflictsForm({
  values,
  shiftType,
  tractor,
  onClose,
  onSubmit,
  loading,
}: {
  loading?: boolean
  onSubmit: (formValues: EquipmentConflictsFormInit) => void
  onClose: () => void
  tractor: components['schemas']['TractorView']
  values: ScheduleItemWithConflicts[]
  shiftType: 'am' | 'pm'
}) {
  const [form] = Form.useForm<EquipmentConflictsFormInit>()
  useEffect(() => {
    if (values.length) {
      setTimeout(() => form.validateFields({ recursive: true }), 100)
    }
  }, [values, form.validateFields])
  if (!values.length) return null
  const initialValues: EquipmentConflictsFormInit = {
    items: values.map((x) => ({
      ...x,
      tractor: { value: x.tractor_id },
      trailer: { value: x.trailer_id },
    })),
    removedItemIds: [],
  }

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={onSubmit}
      clearOnDestroy
      style={{ gap: '1em', display: 'flex', flexDirection: 'column', minWidth: '400px' }}
    >
      <Flex vertical>
        <Flex align='center' justify='space-between'>
          <Texto appearance='hint'>{`Tractor ${tractor.tractor_number}`}</Texto>
          <Texto appearance='hint'>{tractor.depot ?? 'No Location'}</Texto>
        </Flex>
        <Flex align='center' justify='space-between'>
          <Flex gap={6}>
            {shiftType === 'am' ? <AmIcon /> : <PmIcon />}
            <Texto category='h3'>{shiftType === 'am' ? 'Morning Shift' : 'Evening Shift'}</Texto>
          </Flex>
          <Flex gap={6}>
            <CalendarOutlined />
            <Texto>{values[0].start.format('MMMM DD, YYYY')}</Texto>
          </Flex>
        </Flex>
      </Flex>
      <Form.List name='items'>
        {(fields, { remove }) => (
          <>
            {fields.map(({ key, name }) => (
              <Form.Item key={key} shouldUpdate noStyle>
                {({ getFieldValue, setFieldValue }) => (
                  <ScheduleItemRow
                    value={getFieldValue(['items', name])}
                    key={key}
                    index={name}
                    onRemove={({ index, id }) => {
                      const current = getFieldValue('removedItemIds') ?? []
                      setFieldValue('removedItemIds', [...current, id])
                      remove(index)
                    }}
                  />
                )}
              </Form.Item>
            ))}
          </>
        )}
      </Form.List>
      <Flex style={{ flexDirection: 'row-reverse', width: '100%' }} gap={6}>
        <Form.Item hidden name='removedItemIds'>
          <Input />
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldsValue }) => (
            <GraviButton
              loading={loading}
              theme2
              htmlType='submit'
              disabled={isEqual(initialValues, getFieldsValue(true))}
            >
              Save Changes
            </GraviButton>
          )}
        </Form.Item>
        <GraviButton onClick={onClose}>Cancel</GraviButton>
      </Flex>
    </Form>
  )
}
